<template lang="pug">
  .panel-page-white
    .panel-white.body-1.mx-auto
      FormErrorAlert(:message="message" v-show="message" @dismiss="message = ''")
      PageHeader.pb-2 Transaction Summary Report
        button.btn.btn-light(slot="extra" @click="clearForm") Clear Form
      p.mt-3
        | This report will display a summary of transactions for the selected counties as well as the submitting
        | stores within those counties.
      form(
        @submit.prevent="search"
        autocomplete="off"
      )
        .form-row.mt-3
          .col
            h5.section-header DATE RANGE
        div.section
          p Date range has a maximum range of 90 days. Transactions processed today will not be included in the results.
          .form-row
            .col-4
              .form-group
                label(for="from-date") From
                Datepicker(
                  input-id="from-date"
                  :input-class="{'form-control': true, 'is-invalid': dateDifferenceInvalid || fromDateInvalid}"
                  v-model="fromDate"
                )
                FormError(v-if="dateDifferenceInvalid") Date difference must be less than 90 days and From date should be less than To date.
            .col-4
              .form-group
                label(for="to-date") To
                Datepicker(
                  input-id="to-date"
                  :input-class="{'form-control': true, 'is-invalid': dateDifferenceInvalid || toDateInvalid}"
                  v-model="toDate"
                )
                FormError(v-if="dateDifferenceInvalid") Date difference must be less than 2 years and To date should be less than From date.
        .form-row.mt-3
          .col
            h5.section-header LOCATION
        div.section
          p Select the state(s) you wish to include in your report.
          .form-row
            .col-8
              .form-group
                label(for="states") State(s)
                SuperSelect(
                  taggable
                  :options="filterStates"
                  label-name="name"
                  value-name="abbreviation"
                  v-model="selectedStates"
                  @filter="stateFilter = $event"
                  @input="getCounties"
                  :valid="!selectedStatesInvalid"
                )
        div.section
          .form-row.mt-3(v-for="state in selectedStates")
            .col
              .form-row
                .col
                  p What counties do you want to include within <strong>{{state.name}}</strong>?
              .form-row
                .col-8
                  .form-group
                    label(:for="state.abbreviation + '-counties'")
                    SuperSelect(
                      taggable
                      :options="filteredCounties[state.abbreviation]"
                      :value="selectedCounties[state.abbreviation]"
                      @input="setCounties(state.abbreviation, $event)"
                      labelName="description"
                      valueName="fipsCd"
                      @filter="filterCounties(state.abbreviation, $event)"
                    )
        button.btn.btn-primary(type="submit") Submit
</template>

<script>
import { saveItem, loadForm } from '@/lib/formstore'
import { subDays, format, differenceInCalendarDays, parse } from 'date-fns/esm'
import { globalSuccess, globalError, waitReport } from '@/lib/utils'
import axios from '@/lib/axios'
import CustomCheckbox from '@/components/CustomCheckbox'
import CustomRadio from '@/components/CustomRadio'
import CustomSelect from '@/components/CustomSelect'
import Datepicker from '@/components/Datepicker'
import flatten from 'lodash/flatten'
import FormHeader from '@/components/FormHeader'
import FormErrorAlert from '@/components/FormErrorAlert'
import PageHeader from '@/components/PageHeader'
import states from '../lib/states'
import store from '@/store'
import SuperSelect from '@/components/SuperSelect'
import * as validation from '@/lib/validation'
import stdmsg from '@/lib/stdmsg'

function homeStates() {
  return states.filter(st =>
    store.state.account.states.includes(st.abbreviation),
  )
}

export default {
  name: 'StoreSummarySearch',
  data: function() {
    return {
      fromDate: format(subDays(new Date(), 30), 'MM/dd/yyyy'),
      toDate: format(new Date(), 'MM/dd/yyyy'),
      selectedStates: [],
      stateFilter: '',
      counties: {},
      selectedCounties: {},
      filteredCounties: {},
      message: '',
      ...validation.mapTouched([
        'fromDate',
        'toDate',
        'selectedStates',
        'counties',
      ]),
    }
  },
  mounted: loadForm(
    'storesummary',
    'fromDate',
    'toDate',
    'selectedStates',
    'counties',
    'selectedCounties',
    'filteredCounties',
  ),
  created() {
    // This is a little hacky, but according to https://jira.sdlc.appriss.com/browse/KEYSTONE-1536
    // we need to set the user state to the home state. So we watch the state and wait for the user's
    // states to be set, then set it to the selected states.
    this.$watch(
      () => store.state.account.states,
      () => {
        if (this.selectedStates.length === 0) {
          this.selectedStates = homeStates()
          this.getCounties(this.selectedStates)
        }
      },
    )
  },
  methods: {
    search() {
      this.markAllTouched()
      if (
        this.selectedStatesInvalid ||
        this.fromDateInvalid ||
        this.toDateInvalid ||
        this.dateDifferenceInvalid ||
        this.countiesInvalid
      ) {
        scrollTo(0, 0)
        this.message = stdmsg.formError
        if (this.countiesInvalid)
          this.message =
            'There is an error on this form. You must select between one and twenty counties.'
        return
      }
      this.message = ''
      saveItem('storesummary', 'searchCriteria', this.searchCriteria)
      axios()
        .post('/api/store_summary/summary', this.searchCriteria)
        .then(resp => {
          let id = resp.data.id
          waitReport(id, {
            name: 'transaction_summary_results1',
            params: { id },
          })
        })
        .catch(err => {
          globalError(err)
        })
      scrollTo(0, 0)
    },
    clearForm() {
      this.markAllNotTouched()
      this.message = ''
      this.fromDate = ''
      this.toDate = ''
      this.selectedStates = []
      this.counties = {}
      this.selectedCounties = {}
      this.filteredCounties = {}
    },
    getCounties(states) {
      for (let { abbreviation } of states) {
        axios()
          .get('/api/misc/counties', {
            params: { state: abbreviation },
          })
          .then(resp => {
            this.$set(this.counties, abbreviation, resp.data)
            this.$set(this.filteredCounties, abbreviation, resp.data)
          })
      }
    },
    setCounties(abbr, counties) {
      this.$set(this.$set(this.selectedCounties, abbr, counties))
    },
    filterCounties(abb, filterText) {
      let all = this.counties[abb]
      this.$set(
        this.filteredCounties,
        abb,
        all.filter(co =>
          co.description.toLowerCase().includes(filterText.toLowerCase()),
        ),
      )
    },
    ...validation.validationHelpers(),
  },
  computed: {
    ...validation.mapRequired(['fromDate', 'toDate']),
    dateDifferenceInvalid() {
      if (!this.fromDateTouched || !this.toDateTouched) return false
      if (this.dateDifference && this.dateDifference > 90) return true
      if (this.dateDifference < 0) return true
      return false
    },
    selectedStatesInvalid() {
      if (!this.selectedStatesTouched) return false
      return this.selectedStates.length === 0
    },
    countiesInvalid() {
      return (
        this.countiesTouched &&
        (this.flatCounties.length < 1 || this.flatCounties.length > 20)
      )
    },
    jsFromDate() {
      return parse(this.fromDate, 'MM/dd/yyyy', new Date())
    },
    jsToDate() {
      return parse(this.toDate, 'MM/dd/yyyy', new Date())
    },
    dateDifference() {
      return differenceInCalendarDays(this.jsToDate, this.jsFromDate)
    },
    filterStates() {
      return states.filter(st =>
        st.name.toLowerCase().includes(this.stateFilter.toLowerCase()),
      )
    },
    selectedCountiesForSelectedStates() {
      let selectedStateCds = this.selectedStates.map(st => st.abbreviation)
      let selectedCountyStateCds = Object.keys(this.selectedCounties)
      let newObj = {}
      selectedCountyStateCds.forEach(sc => {
        if (selectedStateCds.includes(sc)) {
          newObj[sc] = this.selectedCounties[sc]
        }
      })
      return newObj
    },
    flatCounties() {
      return flatten(
        Object.entries(this.selectedCountiesForSelectedStates).map(o => o[1]),
      )
    },
    // These are the actual final search criteria sent to the API
    searchCriteria() {
      let ret = {}
      if (this.selectedStates.length > 0)
        ret.states = this.selectedStates.map(st => st.abbreviation)
      if (this.flatCounties.length > 0) {
        ret.fipsCds = []
        // Ignore eslint warning about side effects
        // eslint-disable-next-line
        this.flatCounties.forEach(co => {
          ret.fipsCds.push(co.fipsCd)
        })
      }
      let attrs = ['fromDate', 'toDate']
      attrs.forEach(attr => {
        if (this[attr] && this[attr].length > 0) ret[attr] = this[attr]
      })
      return ret
    },
  },
  components: {
    PageHeader,
    CustomRadio,
    CustomCheckbox,
    Datepicker,
    CustomSelect,
    FormHeader,
    SuperSelect,
    FormErrorAlert,
  },
}
</script>

<style lang="scss" scoped>
.section-header {
  border-bottom: 1px solid #e6e6e6;
  padding-bottom: 0.6rem;
}

.section {
  background-color: #f8f8f8;
  padding: 1rem;
}
</style>
